import { createTheme, PaletteMode, Shadows } from '@mui/material/styles';

declare module '@mui/material/Paper' {
  interface PaperPropsVariantOverrides {
    highlighted: true;
  }
}
declare module '@mui/material/styles/createPalette' {
  interface ColorRange {
    50: string;
    100: string;
    200: string;
    300: string;
    400: string;
    500: string;
    600: string;
    700: string;
    800: string;
    900: string;
  }

  interface PaletteColor extends ColorRange { }

  interface Palette {
    baseShadow: string;
  }
}

const defaultTheme = createTheme();

const customShadows: Shadows = [...defaultTheme.shadows];

// Trust Blue (Primary)
const primary = {
  50: '#e3f2fd',
  100: '#bbdefb',
  200: '#90caf9',
  300: '#64b5f6',
  400: '#42a5f5',
  500: '#1976d2', // main
  600: '#1565c0', // dark
  700: '#0d47a1',
  800: '#0a3d91',
  900: '#072b6b',
};

// Trust Green (Secondary)
const secondary = {
  50: '#e8f5e9',
  100: '#c8e6c9',
  200: '#a5d6a7',
  300: '#81c784',
  400: '#4caf50',
  500: '#388e3c', // main
  600: '#2e7d32', // dark
  700: '#1b5e20',
  800: '#145214',
  900: '#0d3a0d',
};

// Risk Red (Error)
const error = {
  50: '#ffebee',
  100: '#ffcdd2',
  200: '#ef9a9a',
  300: '#e57373',
  400: '#ef5350',
  500: '#d32f2f', // main
  600: '#c62828', // dark
  700: '#b71c1c',
  800: '#8e1818',
  900: '#5c1010',
};

// Warning Orange
const warning = {
  50: '#fff3e0',
  100: '#ffe0b2',
  200: '#ffcc80',
  300: '#ffb74d',
  400: '#ff9800',
  500: '#ed6c02', // main
  600: '#e65100', // dark
  700: '#bf360c',
  800: '#8c2a09',
  900: '#5a1a06',
};

// Info Blue
const info = {
  50: '#e3f2fd',
  100: '#bbdefb',
  200: '#90caf9',
  300: '#64b5f6',
  400: '#03a9f4',
  500: '#0288d1', // main
  600: '#01579b', // dark
  700: '#014a7f',
  800: '#013c63',
  900: '#012e47',
};

// Success Green
const success = {
  50: '#e8f5e9',
  100: '#c8e6c9',
  200: '#a5d6a7',
  300: '#81c784',
  400: '#4caf50',
  500: '#2e7d32', // main
  600: '#1b5e20', // dark
  700: '#145214',
  800: '#0d3a0d',
  900: '#072407',
};

// Theme color primitives
export const gray = {
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D1D5DB',
  400: '#9CA3AF',
  500: '#6B7280',
  600: '#4B5563',
  700: '#374151',
  800: '#1F2937',
  900: '#111827',
};

export const red = {
  50: '#FEF2F2',
  100: '#FEE2E2',
  200: '#FECACA',
  300: '#FCA5A5',
  400: '#F87171',
  500: '#EF4444',
  600: '#DC2626',
  700: '#B91C1C',
  800: '#991B1B',
  900: '#7F1D1D',
};

export const green = {
  50: '#F0FDF4',
  100: '#DCFCE7',
  200: '#BBF7D0',
  300: '#86EFAC',
  400: '#4ADE80',
  500: '#22C55E',
  600: '#16A34A',
  700: '#15803D',
  800: '#166534',
  900: '#14532D',
};

export const orange = {
  50: '#FFF7ED',
  100: '#FFEDD5',
  200: '#FED7AA',
  300: '#FDBA74',
  400: '#FB923C',
  500: '#F97316',
  600: '#EA580C',
  700: '#C2410C',
  800: '#9A3412',
  900: '#7C2D12',
};

export const brand: Record<number | string, any> = {
  // Numeric shade variants for compatibility with color utilities
  50: '#EFF6FF',
  100: '#DBEAFE',
  200: '#BFDBFE',
  300: '#93C5FD',
  400: '#60A5FA',
  500: '#3B82F6',
  600: '#2563EB',
  700: '#1D4ED8',
  800: '#1E40AF',
  900: '#1E3A8A',
  // Named colors
  primary: '#2563EB',
  secondary: '#4F46E5',
  accent: '#7C3AED',
  background: '#FFFFFF',
  surface: '#F9FAFB',
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    disabled: '#9CA3AF',
  },
  border: '#E5E7EB',
  error: '#EF4444',
  success: '#22C55E',
  warning: '#F97316',
  info: '#3B82F6',
};

// Theme spacing primitives
const spacing = (factor: number) => `${factor * 8}px`;

// Theme breakpoint primitives
const breakpoints = {
  xs: '0px',
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  '2xl': '1536px',
};

// Theme transition primitives
const transitions = {
  duration: {
    shortest: 150,
    shorter: 200,
    short: 250,
    standard: 300,
    complex: 375,
    enteringScreen: 225,
    leavingScreen: 195,
  },
  easing: {
    easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
    easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
    easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
    sharp: 'cubic-bezier(0.4, 0, 0.6, 1)',
  },
};

// Theme design tokens
const getDesignTokens = (mode: PaletteMode) => ({
  palette: {
    mode,
    primary: {
      main: brand.primary,
      light: '#60A5FA',
      dark: '#1D4ED8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: brand.secondary,
      light: '#818CF8',
      dark: '#3730A3',
      contrastText: '#FFFFFF',
    },
    error: {
      main: brand.error,
      light: '#FCA5A5',
      dark: '#B91C1C',
      contrastText: '#FFFFFF',
    },
    warning: {
      main: brand.warning,
      light: '#FED7AA',
      dark: '#C2410C',
      contrastText: '#FFFFFF',
    },
    info: {
      main: brand.info,
      light: '#93C5FD',
      dark: '#1E40AF',
      contrastText: '#FFFFFF',
    },
    success: {
      main: brand.success,
      light: '#86EFAC',
      dark: '#15803D',
      contrastText: '#FFFFFF',
    },
    grey: gray,
    text: {
      primary: brand.text.primary,
      secondary: brand.text.secondary,
      disabled: brand.text.disabled,
    },
    background: {
      default: brand.background,
      paper: brand.surface,
    },
  },
  typography: {
    fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
    fontSize: 16,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: {
      fontSize: 36,
      fontWeight: 700,
      lineHeight: 1.2,
    },
    h2: {
      fontSize: 30,
      fontWeight: 700,
      lineHeight: 1.3,
    },
    h3: {
      fontSize: 24,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: 20,
      fontWeight: 600,
      lineHeight: 1.4,
    },
    h5: {
      fontSize: 18,
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h6: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.5,
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      lineHeight: 1.5,
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontSize: 14,
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'none',
    },
    caption: {
      fontSize: 12,
      fontWeight: 400,
      lineHeight: 1.5,
    },
    overline: {
      fontSize: 12,
      fontWeight: 500,
      lineHeight: 1.5,
      textTransform: 'uppercase',
    },
  },
  spacing,
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    '0 25px 50px -12px rgb(0 0 0 / 0.25)',
    'inset 0 2px 4px 0 rgb(0 0 0 / 0.05)',
  ],
  transitions: {
    duration: transitions.duration,
    easing: transitions.easing,
    create: (props: string | string[] = ['all'], options: any = {}) => {
      const {
        duration = transitions.duration.standard,
        easing = transitions.easing.easeInOut,
        delay = 0,
      } = options;

      return (Array.isArray(props) ? props : [props])
        .map(
          (prop) =>
            `${prop} ${duration}ms ${easing} ${delay}ms`
        )
        .join(',');
    },
  },
  breakpoints: {
    values: breakpoints,
    up: (key: keyof typeof breakpoints) => `@media (min-width:${breakpoints[key]})`,
    down: (key: keyof typeof breakpoints) => `@media (max-width:${breakpoints[key]})`,
    between: (start: keyof typeof breakpoints, end: keyof typeof breakpoints) =>
      `@media (min-width:${breakpoints[start]}) and (max-width:${breakpoints[end]})`,
  },
});

export const colorSchemes = {
  light: getDesignTokens('light').palette,
  dark: getDesignTokens('dark').palette,
};

// Export theme tokens for light mode
export const lightThemeTokens = getDesignTokens('light');